import { gql, useLazyQuery } from "@apollo/client";
import { useContext, useEffect, useState } from "react";

import { FRAGMENT_PROPERTY_CARD } from "../../Property/PropertyCard/PropertyCard.hook";
import { isValidCharacters } from "../../../Utils/Functions";
import { useDebouncedCallback } from "../../../GlobalHooks/useDebounce";
import useLocationExtraData from "src/hooks/locationExtraData/useLocationExtraData";
import { fetchSearchResults, getLocations } from "src/services/search";
import { formattedLocationsFilter } from "src/utils/Functions";
import { PropertyData } from "src/contexts/PropertyData/PropertyDataProvider";
import { ConfigStateContext } from "shared-components/Contexts/Configurations/context";

const KEYWORD_LOCATION_QUERY = gql`
	query Location($strSearch: String!) {
		searchLocation(searchTerm: $strSearch) {
			... on Estate {
				id
				name
			}
			... on Neighborhood {
				id
				name
				estate {
					id
					name
				}
			}
			__typename
		}
	}
`;

const PROJECTS_COUNT = 3;
const PROJECTS_QUERY = gql`
	query SearchProject($title: String) {
		searchListing(params: { operation_type_id: 1, projects:true, projectTitle: $title }, first: ${PROJECTS_COUNT}, page: 1) {
			data {
				${FRAGMENT_PROPERTY_CARD.query()}
			}
		}
	}
`;
const REF_QUERY = gql`
	query searchByRef($code: String!) {
		searchByRef(refCode: $code) {
			... on Project {
				id
				title
				link
				isEspecial
			}
			... on Property {
				${FRAGMENT_PROPERTY_CARD.query()}
			}
		}
	}
`;

export interface KeywordLocationProps {
	className?: string;
	searchByProject?: boolean;
	searchByReference?: boolean;
	isSearchForHome?: boolean;
	filterChanged?: ({ }: any) => void;
	handleLocations?: ([]) => void;
	locations?: any;
	changeKeyword?: any;
	isMobile?: boolean;
	changeFilters: any;
	filtersTags: any;
}

export const useKeywordLocation = ({
	searchByReference,
	searchByProject,
	locations = [],
	changeFilters,
	filtersTags
}: KeywordLocationProps) => {
	const { setCoordinates } = useLocationExtraData();
	const [location, setLocation] = useState(null);
	const [resultByRef, setResultByRef] = useState<PropertyData | null>(null);
	const [typing, setTyping] = useState(false);
	const [locationOptions, setLocationOptions] = useState(null);
	const [loadingLocations, setLoadingLocations] = useState(false);
	const [loadingReferences, setLoadingReferences] = useState(false);
	const [debounceTimeOut, setDebounceTimeOut] = useState(null);
	const [keyword, setKeyWord] = useState<string>("");
	const { country_code, main_domain } = useContext(ConfigStateContext);

	useEffect(() => updateFilters(), [location]);

	useEffect(() => {
		if (debounceTimeOut != null) {
			clearTimeout(debounceTimeOut);
		}
		if (keyword?.length > 1) {
			setLoadingLocations(true);
			setResultByRef(null);
			setLocationOptions([]);
			
			setDebounceTimeOut(
				setTimeout(async () => {
					if (country_code !== 'CO') {
						// 1. Búsqueda por referencia primero
						setLoadingReferences(true);
						const refSearch = await fetchSearchResults(
							{
								rows: 100,
								params: { code: keyword },
								page: 1,
								source: 10,
							},
							main_domain
						);

						if (refSearch?.property) {
							setResultByRef(refSearch.property);
							setLocationOptions([]);
							setLoadingReferences(false);
							setTyping(false);
							return;
						}
						setLoadingReferences(false);
					}
					getLocations(keyword)
						.then(data => {
							setLocationOptions(data);
							setResultByRef(null);
							setLoadingLocations(false);
						})
						.catch(e => {
							console.log(e);
						});
				}, 500)
			);
			setLoadingLocations(false);
		}
	}, [keyword]);


	// hook functions
	const onSelect = (val, opt) => { //refactorizar donde se llame esta función para quitar el val, evaluar si esto causa algún error.
		setLocation(opt);
	};

	const onSearch = (val: string) => {
		setTyping(true);
		val = val.replace(/<[^>]*>/g, ' ');
		if (isValidCharacters(val) || val == "") {
			setKeyWord(val);
			search(val);
		}
		setTyping(false);
	};

	const [search] = useDebouncedCallback((val: string) => {
		if (val && val.length > 0) {
			searchQuery({ variables: { strSearch: val } }).then(() => {setTyping(false)});
		}
		if (searchByProject && val && val.length > 0) projectQuery({ variables: { title: val } }).then(() => {setTyping(false)});
		if (searchByReference && val && val.length >= 5) refQuery({ variables: { code: val } }).then(() => {setTyping(false)});
	}, 200);

	const [searchQuery, { data, loading }] = useLazyQuery(KEYWORD_LOCATION_QUERY);
	const [projectQuery, { data: dataProjects, loading: loadingProjects }] = useLazyQuery(
		PROJECTS_QUERY
	);

	const [refQuery, { data: dataRef, loading: loadingRef }] = useLazyQuery(REF_QUERY);

	const handleMultipleLocations = (location) => {
		const selectedLocation =  Array.isArray(filtersTags?.locations) ? filtersTags?.locations : [filtersTags?.locations];

		if (selectedLocation && !!selectedLocation.length) {
			const newLocationFormatted = formattedLocationsFilter(location);

			if (location?.estate?.name === selectedLocation[0]?.value[0]?.estate?.name) {
				const updatedLocations = [
					...selectedLocation[0].value, 
					newLocationFormatted[0]
				];

				changeFilters({
					locations: [
						{
							text: "locations",
							value: updatedLocations,
						},
					]
				});
			} else {
				changeFilters({
					locations: [
						{
							text: "locations",
							value: newLocationFormatted,
						},
					]
				});
			}
		} else {
			changeFilters({
				locations: [
					{
						text: "locations",
						value: formattedLocationsFilter(location),
					},
				]
			});
		}
	}

	const updateFilters = () => {
		if (location) {
			if (location.__typename == "keyword") {
				if (locations.length > 0) {
					const firstLocation = locations[0];

					handleMultipleLocations(firstLocation);

					if (firstLocation.location_point?.coordinates) setCoordinates({
						lat: firstLocation.location_point.coordinates[1],
						lng: firstLocation.location_point.coordinates[0],
					})
				} else {
					changeFilters({
						searchstring: { text: "Palabra Clave: " + location.name, value: location.name },
					});
				}
			} else if (location.type) {
				const locationFilter = Array.isArray(filtersTags?.locations) ? filtersTags?.locations : [filtersTags?.locations]
				const alreadyExists = locationFilter[0]?.value.some(
					(loc) => loc.id === location.id
				);

				if (!alreadyExists) handleMultipleLocations(location);

				if (location.location_point?.coordinates) setCoordinates({
					lat: location.location_point.coordinates[1],
					lng: location.location_point.coordinates[0],
				})
			} else {
				changeFilters({
					searchstring: null,
					locations: [],
				});
			}
		}
	};

	// hook output API
	return {
		keyword,
		setKeyWord,
		location,
		show: true,
		onSearch,
		onSelect,
		searchLocationQuery: searchQuery,
		searchResults: locationOptions,
		refResults: resultByRef,
		projectsResults: dataProjects,
		searchLoading: keyword !== '' && (loading || loadingReferences || loadingProjects || typing || loadingLocations),
	};
};

